// @flow strict

// $FlowFixMe[nonstrict-import]
import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {
  TimeOff,
  BookingSlot,
  AgentSettings,
  AgentApiParams,
} from 'src/types/scheduler/settings.js';

//$FlowFixMe[untyped-import]
import {camel, snake} from 'src/utils';
// $FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';
import {selectAgentApiParams} from 'src/selectors/scheduler/settings';
import {getSortedTimeOffs} from 'src/components/scheduler/settings/utils';

// action types constants
export const SET_AGENT_SETTINGS = 'scheduling/settings/setAgentSettings';
export const SET_AGENT_API_PARAMS = 'scheduling/settings/setAgentApiParams';
export const SET_USER_PRINCIPAL_NAME =
  'scheduling/settings/setUserPrincipalName';
export const SET_DAILY_DIGEST = 'scheduling/settings/setDailyDigest';
export const SET_MAX_EVENTS_PER_DAY = 'scheduling/settings/setMaxEventsPerDay';
export const SET_TIMEZONE = 'scheduling/settings/setTimezone';
export const SET_BOOKING_HOURS = 'scheduling/settings/setBookingHours';
export const SET_UPDATED_TIME_OFFS = 'scheduling/settings/setUpdatedTimeOffs';
export const SET_AGENT_LANGUAGE = 'scheduling/settings/setAgentLanguage';

type SchedulerAgentSettingsPayload = AgentSettings;

// action types
export type SchedulerSettingsActions =
  | SetAgentSettingsAction
  | SetUserPrincipalNameAction
  | SetAgentApiParamsAction
  | SetDailyDigestAction
  | SetMaxEventsPerDayAction
  | SetTimezoneAction
  | SetBookingHoursAction
  | SetUpdatedTimeOffsAction;

export type SetAgentSettingsAction = {
  type: typeof SET_AGENT_SETTINGS,
  payload: SchedulerAgentSettingsPayload,
};

export const setAgentSettings = (
  payload: SchedulerAgentSettingsPayload,
): SetAgentSettingsAction => ({
  type: SET_AGENT_SETTINGS,
  payload,
});

export type SetUserPrincipalNameAction = {
  type: typeof SET_USER_PRINCIPAL_NAME,
  payload: string,
};

export type SetAgentLanguageAction = {
  type: typeof SET_AGENT_LANGUAGE,
  payload: string,
};

export const setAgentLanguage = (
  agentLanguage: string,
): SetAgentLanguageAction => ({
  type: SET_AGENT_LANGUAGE,
  payload: agentLanguage,
});

export const setUserPrincipalName = (
  upn: string,
): SetUserPrincipalNameAction => ({
  type: SET_USER_PRINCIPAL_NAME,
  payload: upn,
});

export type SetDailyDigestAction = {
  type: typeof SET_DAILY_DIGEST,
  payload: boolean,
};

export const setDailyDigest = (dailyDigest: boolean): SetDailyDigestAction => ({
  type: SET_DAILY_DIGEST,
  payload: dailyDigest,
});

export type SetAgentApiParamsAction = {
  type: typeof SET_AGENT_API_PARAMS,
  payload: AgentApiParams,
};

export const setAgentApiParams = (
  payload: AgentApiParams,
): SetAgentApiParamsAction => ({
  type: SET_AGENT_API_PARAMS,
  payload,
});

export type SetMaxEventsPerDayAction = {
  type: typeof SET_MAX_EVENTS_PER_DAY,
  payload: number,
};

export const setMaxEventsPerDay = (
  payload: number,
): SetMaxEventsPerDayAction => ({
  type: SET_MAX_EVENTS_PER_DAY,
  payload,
});

export type SetTimezoneAction = {
  type: typeof SET_TIMEZONE,
  payload: string,
};

export const setTimezone = (timezone: string): SetTimezoneAction => ({
  type: SET_TIMEZONE,
  payload: timezone,
});

export type SetBookingHoursAction = {
  type: typeof SET_BOOKING_HOURS,
  payload: BookingSlot[],
};

export const setBookingHours = (
  bookingHours: BookingSlot[],
): SetBookingHoursAction => ({
  type: SET_BOOKING_HOURS,
  payload: bookingHours,
});

export type SetUpdatedTimeOffsAction = {
  type: typeof SET_UPDATED_TIME_OFFS,
  payload: TimeOff[],
};

export const setUpdatedTimeOffs = (
  timeOffs: TimeOff[],
): SetUpdatedTimeOffsAction => ({
  type: SET_UPDATED_TIME_OFFS,
  payload: timeOffs,
});

export const fetchAgentSettings =
  (payload: AgentApiParams): ThunkAction<> =>
  async (dispatch: Dispatch) => {
    try {
      const result = await dispatch(
        reduxApi.get('scheduler/agent/settings', snake(payload)),
      );

      const normalizedResult = {
        ...result,
        timezone: result.timezone || '',
        business_hours: result.business_hours || [],
        user_principal_name: result.user_principal_name || '',
        time_off: snake(getSortedTimeOffs(camel(result.time_off))),
      };

      dispatch(setAgentSettings(camel(normalizedResult)));
    } catch (err) {
      throw err;
    }
  };

export const updateAgentLanguage =
  (agentLanguage: string): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());
    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/agent_language',
          snake({...actorApiParams, value: agentLanguage}),
        ),
      );
      dispatch(setAgentLanguage(agentLanguage));
    } catch (err) {
      throw err;
    }
  };

export const updateUserPrincipalName =
  (upn: string): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());
    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/user_principal_name',
          snake({
            ...actorApiParams,
            value: upn,
          }),
        ),
      );
      dispatch(setUserPrincipalName(upn));
    } catch (err) {
      throw err;
    }
  };

export const updateDailyDigest =
  (dailyDigest: boolean): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());

    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/scheduler_enable_daily_digest',
          snake({
            ...actorApiParams,
            value: dailyDigest,
          }),
        ),
      );
      dispatch(setDailyDigest(dailyDigest));
    } catch (err) {
      throw err;
    }
  };

export const updateMaxEventsPerDay =
  (maxEventsPerDay: number): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());
    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/scheduler_max_events_per_day',
          snake({
            ...actorApiParams,
            value: maxEventsPerDay,
          }),
        ),
      );
      dispatch(setMaxEventsPerDay(maxEventsPerDay));
    } catch (err) {
      throw err;
    }
  };

export const updateBookingHours =
  (bookingHours: BookingSlot[]): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());
    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/business_hours',
          snake({
            ...actorApiParams,
            value: bookingHours,
          }),
        ),
      );
      dispatch(setBookingHours(bookingHours));
    } catch (err) {
      throw err;
    }
  };

export const updateTimezone =
  (timezone: string): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());

    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/timezone',
          snake({
            ...actorApiParams,
            value: timezone,
          }),
        ),
      );
      dispatch(setTimezone(timezone));
    } catch (err) {
      throw err;
    }
  };

export const saveTimeOffs =
  (timeOffs: TimeOff[]): ThunkAction<> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const actorApiParams = selectAgentApiParams(getState());
    const sortedTimeOffs = getSortedTimeOffs(timeOffs);

    try {
      await dispatch(
        reduxApi.put(
          'scheduler/agent/setting/time_off',
          snake({
            ...actorApiParams,
            value: sortedTimeOffs,
          }),
        ),
      );
      dispatch(setUpdatedTimeOffs(sortedTimeOffs));
    } catch (err) {
      throw err;
    }
  };

// moved all api calls from settings page to here - not being used in redux

// fetch calendar auth url
export const fetchCalendarAuthUrl =
  (): ThunkAction<> => async (dispatch: Dispatch) => {
    try {
      const result = await dispatch(
        reduxApi.get('scheduler/accounts/auth/url'),
      );
      return result?.auth_url;
    } catch (err) {
      throw err;
    }
  };

// if we are adding a virtual calendar and we are already connected to a calendar, we need to disconnect the existing calendar
export const removeExistingCalendar =
  (): ThunkAction<> => async (dispatch: Dispatch) => {
    try {
      await dispatch(reduxApi.del('scheduler/accounts/auth'));
    } catch (err) {
      throw err;
    }
  };

// connect virtual calendar
export const addVirtualCalendar =
  (timezone: string): ThunkAction<> =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.post('scheduler/accounts/virtual-calendar', {timezone}),
      );
    } catch (err) {
      throw err;
    }
  };
